import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Article {
  id: string;
  title: string;
  description: string;
  image_url: string;
  category: string;
  created_at: string;
  slug: string;
}

const BlogSection: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLatestArticles = async () => {
      try {
        const { data, error } = await supabase
          .from('articles')
          .select('id, title, description, image_url, category, created_at, slug')
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) throw error;
        setArticles(data || []);
      } catch (error) {
        console.error('Error fetching articles:', error);
        setError('Erreur lors du chargement des articles');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLatestArticles();
  }, []);

  if (isLoading) {
    return (
      <section id="blog" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Notre Blog</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Chargement des articles...
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="blog" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Notre Blog</h2>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="blog" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Notre Blog</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Découvrez nos derniers articles, conseils et astuces pour optimiser vos processus de recrutement et attirer les meilleurs talents.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {articles.map(article => (
            <Link 
              key={article.id} 
              to={`/resources/${article.slug}`} 
              className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:scale-105"
            >
              {article.image_url && (
                <div className="h-48 overflow-hidden">
                  <img 
                    src={article.image_url} 
                    alt={article.title} 
                    className="w-full h-full object-cover transition-transform hover:scale-110"
                  />
                </div>
              )}
              <div className="p-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs font-medium bg-gradient-primary text-white px-2 py-1 rounded-full">
                    {article.category || 'Article'}
                  </span>
                  <div className="flex items-center text-gray-500 text-xs">
                    <Calendar className="w-3 h-3 mr-1" />
                    <span>{new Date(article.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3 line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {article.description}
                </p>
                <div className="flex justify-end">
                  <span className="link-gradient text-sm font-medium flex items-center">
                    <span>Lire plus</span>
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        
        <div className="text-center mt-10">
          <Link 
            to="/resources" 
            className="btn-primary inline-block"
          >
            Voir tous les articles
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;